// ts-rest contract for the View per-page typed endpoints.
// Firm-overview returns the compliance / breach / KYC rows whose counts the
// page subtitle reports (breach + KYC now read through ctx.read). The
// view_aggregates / dashboard_aggregates read-model caches were dropped
// (#151, ADR-0008): the financial/investment/board pages return empty until
// their ObjectTypes are modelled.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionResponses } from './agent';

const c = initContract();

// `passthrough` is now reserved ONLY for the unmodelled view-aggregate rows
// (financial / investment / board performance) whose ObjectTypes are not yet
// modelled (#151) — those endpoints return an empty `rows: []`. The
// fund-management / trust-detail rows below are REAL camelCase DTOs: the
// service projects the typed Drizzle rows onto them via `toDto` mappers, so
// snake_case stays in the DB layer (the Drizzle column names) and never reaches
// the wire (PRD #278: contract fields are camelCase).
const passthrough = z.record(z.unknown());

// --- Fund-management row DTOs ------------------------------------------------
// camelCase wire keys mirror the Drizzle TS-property keys; the service `toDto`
// mappers drop the snake_case column aliases the legacy `table-rows` row shim
// used to dual-key. Field sets are the columns the fund-management screens read.

export const investmentTrustRowSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    ticker: z.string().optional(),
    peerGroup: z.string().optional(),
    reportingCurrency: z.string().optional(),
    benchmark: z.string().nullable().optional(),
    listedSince: z.string().optional(),
});
export type InvestmentTrustRow = z.infer<typeof investmentTrustRowSchema>;

export const exposureSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    navPerShare: z.number(),
    sharePrice: z.number(),
    discountToNavPct: z.number(),
    gearingPct: z.number(),
    sectorExposure: z.array(z.object({ sector: z.string(), weightPct: z.number() })),
    topHoldingConcentrationPct: z.number(),
    topHoldingSecurityName: z.string(),
    shareholderConcentrationTop5Pct: z.number(),
});
export type ExposureSnapshotRow = z.infer<typeof exposureSnapshotRowSchema>;

export const discountSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    navPerShare: z.number(),
    sharePrice: z.number(),
    discountToNavPct: z.number(),
    peerGroupDiscountPct: z.number().nullable().optional(),
});
export type DiscountSnapshotRow = z.infer<typeof discountSnapshotRowSchema>;

export const gearingSnapshotRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    asOf: z.string(),
    netGearingPct: z.number(),
    policyLimitPct: z.number(),
    covenantLimitPct: z.number(),
    facilityLimitGbp: z.number(),
    facilityDrawnGbp: z.number(),
    costOfDebtPct: z.number(),
    cashPct: z.number(),
});
export type GearingSnapshotRow = z.infer<typeof gearingSnapshotRowSchema>;

export const policyClauseRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    clauseRef: z.string(),
    ruleKind: z.string(),
    title: z.string(),
    statement: z.string(),
    metric: z.string(),
    subject: z.string().nullable().optional(),
    direction: z.enum(['max', 'min', 'band']),
    limitPct: z.number().nullable(),
    lowerBoundPct: z.number().nullable(),
    upperBoundPct: z.number().nullable(),
    breachSeverity: z.enum(['approaching', 'breach', 'material']),
    sourceDocument: z.string(),
    effectiveFrom: z.string(),
});
export type PolicyClauseRow = z.infer<typeof policyClauseRowSchema>;

export const shareholderRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    holderName: z.string(),
    // holderType values are domain enum strings stored in the DB (snake_case
    // values are agreed wire values, not casing-drift keys).
    holderType: z.enum(['institutional', 'retail', 'wealth_manager', 'insider']),
    holdingPct: z.number(),
    sharesHeld: z.number(),
    changeQoqPct: z.number(),
    notifiable: z.boolean(),
    asOf: z.string(),
});
export type ShareholderRow = z.infer<typeof shareholderRowSchema>;

export const continuationVoteRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    voteDate: z.string(),
    cadenceYears: z.number(),
    approvalThresholdPct: z.number(),
    discountTriggerPct: z.number().nullable().optional(),
    status: z.enum(['scheduled', 'in_campaign', 'passed', 'failed']),
    forecastOutcome: z.enum(['pass_likely', 'pass_marginal', 'at_risk']).nullable().optional(),
    forecastSupportPct: z.number().nullable().optional(),
    notes: z.string().nullable().optional(),
});
export type ContinuationVoteRow = z.infer<typeof continuationVoteRowSchema>;

export const brokerCoverageRowSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    broker: z.string(),
    analyst: z.string(),
    rating: z.enum(['buy', 'add', 'hold', 'reduce', 'sell']),
    targetPrice: z.number(),
    targetPriceHistory: z.array(z.object({ asOf: z.string(), targetPrice: z.number() })),
    lastNoteTitle: z.string(),
    lastNoteDate: z.string(),
    isHouseBroker: z.boolean(),
});
export type BrokerCoverageRow = z.infer<typeof brokerCoverageRowSchema>;

export const fundReportDraftRowSchema = z.object({
    id: z.string(),
    investmentTrustId: z.string(),
    kind: z.string(),
    title: z.string().optional(),
    summary: z.string().optional(),
    body: z.string().optional(),
    reviewStatus: z
        .enum(['pending', 'approved', 'rejected', 'revision_requested'])
        .nullable()
        .optional(),
});
export type FundReportDraftRow = z.infer<typeof fundReportDraftRowSchema>;

// --- Firm Overview row DTOs (tight — server projects ontology rows to these) -
// The page subtitle counts open breaches, overdue KYC and open compliance
// alerts; these are the minimal honest shapes the controller projects (the
// alert vocabulary is mapped to the display strings the page renders).
export const firmOverviewBreachSchema = z.object({
    status: z.string(),
});
export const firmOverviewKycReviewSchema = z.object({
    status: z.string(),
    daysUntilDue: z.number(),
});
export const firmOverviewComplianceAlertSchema = z.object({
    alertType: z.string(),
    status: z.string(),
});

export const firmOverviewResponseSchema = z.object({
    breachRegister: z.array(firmOverviewBreachSchema),
    kycReviews: z.array(firmOverviewKycReviewSchema),
    complianceAlerts: z.array(firmOverviewComplianceAlertSchema),
});
export type FirmOverviewResponse = z.infer<typeof firmOverviewResponseSchema>;
export type FirmOverviewBreach = z.infer<typeof firmOverviewBreachSchema>;
export type FirmOverviewKycReview = z.infer<typeof firmOverviewKycReviewSchema>;
export type FirmOverviewComplianceAlert = z.infer<typeof firmOverviewComplianceAlertSchema>;

export const viewAggregatesResponseSchema = z.object({
    rows: z.array(passthrough),
});
export type ViewAggregatesResponse = z.infer<typeof viewAggregatesResponseSchema>;

// --- Operational Intelligence DTOs (View > Operational Intelligence) ---------
// Server-aggregated operational signals across the three current-state ops
// surfaces the firm uploads: reconciliation breaks, overdrawn cash and the
// dealing-desk Order Blotter. The service folds the raw rows into these compact
// counts (the controller is the mapping seam — ADR-0008). Money is only ever
// summed within a single currency (never cross-currency), so the overdrawn /
// trade sections key their totals by currency. The snapshot-backed sections
// carry an `available` flag (the SnapshotPointer exists) so the page can tell
// "never uploaded" apart from "uploaded, nothing outstanding".

/** A labelled count — a distribution bucket (by status / side / currency). */
export const opsLabelCountSchema = z.object({
    label: z.string(),
    count: z.number().int(),
});
export type OpsLabelCount = z.infer<typeof opsLabelCountSchema>;

/** Reconciliation-break signal. Always present (breaks are computed rows, not a
 *  snapshot): zero open breaks is an honest, positive value. */
export const opsReconSignalSchema = z.object({
    openBreaks: z.number().int(),
    autoResolved: z.number().int(),
    manuallyResolved: z.number().int(),
    chronicOpen: z.number().int(),
    oldestOpenAgeDays: z.number().int().nullable(),
    // Open breaks split by custodian (RBC / Lloyds / Fund), derived from the
    // `breakReason` prefix.
    byCustodian: z.array(z.object({ custodian: z.string(), open: z.number().int() })),
});
export type OpsReconSignal = z.infer<typeof opsReconSignalSchema>;

/** Overdrawn-cash signal, per current-state cash snapshot. */
export const opsOverdrawnSignalSchema = z.object({
    available: z.boolean(),
    accountCount: z.number().int(),
    unresolvedCount: z.number().int(),
    byCurrency: z.array(
        z.object({
            currency: z.string(),
            accountCount: z.number().int(),
            // Sum of the overdrawn balances for this currency, as a positive
            // magnitude (the underlying posted balances are negative).
            totalOverdrawn: z.number(),
        }),
    ),
});
export type OpsOverdrawnSignal = z.infer<typeof opsOverdrawnSignalSchema>;

/** Dealing-desk Order Blotter signal, per current-state blotter snapshot. */
export const opsTradeSignalSchema = z.object({
    available: z.boolean(),
    orderCount: z.number().int(),
    // Orders not in a terminal status (i.e. not Settled / Cancelled).
    inFlightCount: z.number().int(),
    // In-flight orders whose settlement date has already passed.
    overdueSettlementCount: z.number().int(),
    byStatus: z.array(opsLabelCountSchema),
    bySide: z.array(opsLabelCountSchema),
    byCurrency: z.array(opsLabelCountSchema),
});
export type OpsTradeSignal = z.infer<typeof opsTradeSignalSchema>;

export const operationalIntelligenceResponseSchema = z.object({
    reconciliation: opsReconSignalSchema,
    overdrawnCash: opsOverdrawnSignalSchema,
    trades: opsTradeSignalSchema,
});
export type OperationalIntelligenceResponse = z.infer<typeof operationalIntelligenceResponseSchema>;

// --- Custodian Analytics DTOs (View > Custodian Analytics) -------------------
// Per-custodian feed-coverage signal. The firm receives a position feed from
// each external custodian (RBC, Lloyds, CACEIS, Butterfield); the page reports
// how complete and fresh each feed is. Holdings carry positions (quantity by
// ISIN), not a GBP valuation, so this is deliberately a coverage view, not an
// AUM-by-custodian view (no value is fabricated — ADR-0005). `dataMonth` is the
// scope (YYYY-MM) of the latest ingested feed; `available` is false until a
// custodian's first upload lands.
export const custodianCoverageRowSchema = z.object({
    custodian: z.string(),
    available: z.boolean(),
    dataMonth: z.string().nullable(),
    lastParsedAt: z.string().nullable(),
    positions: z.number().int(),
    securities: z.number().int(),
});
export type CustodianCoverageRow = z.infer<typeof custodianCoverageRowSchema>;

export const custodianAnalyticsResponseSchema = z.object({
    custodians: z.array(custodianCoverageRowSchema),
});
export type CustodianAnalyticsResponse = z.infer<typeof custodianAnalyticsResponseSchema>;

// --- View performance DTOs (issue 352 Workstream A) --------------------------
// Real typed projections over AccountPerformancePeriod / AccountPerformanceReturns
// / FxRate / BenchmarkIndex+Level (replacing the {rows:[]} pass-through). Money
// values are GBP (converted from native currency at the controller seam via the
// active FxRate; a non-GBP account with no loaded rate contributes null, never a
// fabricated figure — ADR-0005). `available` distinguishes "no snapshot uploaded
// yet" (honest empty state) from "loaded but zero". Blank returns are null (the
// page renders a dash "–", never 0).

/** Firm-level portfolio-economics totals for the calc month (Financial Perf). */
export const financialPerformanceFirmSchema = z.object({
    // Sum of portfolio end values (£) — the month-end value, not a billing/AUM
    // fee base. Labelled "value" on the page, not "AUM".
    totalAssetsGbp: z.number().nullable(),
    incomeGbp: z.number().nullable(),
    feesGbp: z.number().nullable(),
    netFlowsGbp: z.number().nullable(),
    accountCount: z.number().int(),
    // Non-GBP accounts that could not be converted (no FX rate loaded) and are
    // therefore excluded from the GBP totals — surfaced so the page is honest.
    unconvertedAccountCount: z.number().int(),
    // Exact-duplicate portfolio rows collapsed before the roll-up (the June
    // Tercero export ships every row twice — a known, business-confirmed glitch).
    duplicateRowsCollapsed: z.number().int(),
});

/** One economics grouping row (per client category or per manager). */
export const financialPerformanceGroupSchema = z.object({
    label: z.string(),
    totalAssetsGbp: z.number().nullable(),
    incomeGbp: z.number().nullable(),
    feesGbp: z.number().nullable(),
    netFlowsGbp: z.number().nullable(),
    accountCount: z.number().int(),
});
export type FinancialPerformanceGroup = z.infer<typeof financialPerformanceGroupSchema>;

export const financialPerformanceResponseSchema = z.object({
    available: z.boolean(),
    dataMonth: z.string().nullable(),
    firm: financialPerformanceFirmSchema,
    byCategory: z.array(financialPerformanceGroupSchema),
    byManager: z.array(financialPerformanceGroupSchema),
});
export type FinancialPerformanceResponse = z.infer<typeof financialPerformanceResponseSchema>;

/** One account's multi-horizon returns (Investment Performance). */
export const accountReturnsRowSchema = z.object({
    accountName: z.string(),
    clientName: z.string().nullable(),
    manager: z.string().nullable(),
    mandate: z.string().nullable(),
    category: z.string().nullable(),
    benchmarkLabel: z.string().nullable(),
    currency: z.string(),
    return1m: z.number().nullable(),
    return3m: z.number().nullable(),
    return6m: z.number().nullable(),
    returnYtd: z.number().nullable(),
    return1y: z.number().nullable(),
    return2y: z.number().nullable(),
    return3y: z.number().nullable(),
    return5y: z.number().nullable(),
    returnSinceInception: z.number().nullable(),
});
export type AccountReturnsRow = z.infer<typeof accountReturnsRowSchema>;

/** Average YTD across the accounts in a client category (Investment Perf chart). */
export const categoryReturnSchema = z.object({
    label: z.string(),
    avgYtdPct: z.number().nullable(),
    accountCount: z.number().int(),
});
export type CategoryReturn = z.infer<typeof categoryReturnSchema>;

export const investmentPerformanceResponseSchema = z.object({
    available: z.boolean(),
    dataMonth: z.string().nullable(),
    // Firm aggregate return derived from AccountPerformancePeriod start/end (GBP),
    // NOT from the Tercero "Return %" columns (which skew on tiny balances).
    firmPeriodReturnPct: z.number().nullable(),
    accounts: z.array(accountReturnsRowSchema),
    byCategory: z.array(categoryReturnSchema),
});
export type InvestmentPerformanceResponse = z.infer<typeof investmentPerformanceResponseSchema>;

/** One ARC band's firm-vs-benchmark comparison (Benchmarking). */
export const benchmarkBandRowSchema = z.object({
    band: z.string(),
    label: z.string(),
    accountCount: z.number().int(),
    // Simple (AUM-unweighted) mean of the accounts' YTD in this band.
    avgAccountYtdPct: z.number().nullable(),
    // The ARC Private Client Index YTD for the band (GBP), from BenchmarkLevel;
    // null until the ARC PCI monthly workbook is loaded.
    benchmarkYtdPct: z.number().nullable(),
    deltaPct: z.number().nullable(),
});
export type BenchmarkBandRow = z.infer<typeof benchmarkBandRowSchema>;

/** One ARC-benchmarked account's YTD vs its band index (Benchmarking). */
export const benchmarkAccountRowSchema = z.object({
    accountName: z.string(),
    clientName: z.string().nullable(),
    band: z.string(),
    label: z.string(),
    currency: z.string(),
    accountYtdPct: z.number().nullable(),
    benchmarkYtdPct: z.number().nullable(),
    deltaPct: z.number().nullable(),
});
export type BenchmarkAccountRow = z.infer<typeof benchmarkAccountRowSchema>;

export const benchmarkingResponseSchema = z.object({
    available: z.boolean(),
    dataMonth: z.string().nullable(),
    // True once the ARC PCI level workbook is loaded so benchmark deltas compute.
    benchmarkLevelsLoaded: z.boolean(),
    // Count of non-ARC / un-benchmarked accounts not shown (footnote only).
    excludedAccountCount: z.number().int(),
    bands: z.array(benchmarkBandRowSchema),
    accounts: z.array(benchmarkAccountRowSchema),
});
export type BenchmarkingResponse = z.infer<typeof benchmarkingResponseSchema>;

export const fundManagementListResponseSchema = z.object({
    trusts: z.array(investmentTrustRowSchema),
    exposures: z.array(exposureSnapshotRowSchema),
    discounts: z.array(discountSnapshotRowSchema),
    gearing: z.array(gearingSnapshotRowSchema),
});
export type FundManagementListResponse = z.infer<typeof fundManagementListResponseSchema>;

// --- Fund-management row / mutation-result DTOs (tight) ----------------------
// `rnsAnnouncementDtoSchema` is both the trust-detail RNS read projection AND
// the materialise mutation's result row. Both mutations dispatch through
// ontology Actions (ADR-0008) and report the row they materialised inside the
// generic `StaffActionResult.data` envelope. The envelope is loose by design
// (ADR-0018), but the rows inside it are typed ontology projections — these
// schemas mirror what the server sends so the frontend gets the real shape
// rather than `Record<string, unknown>`. Declared before the response schema
// that embeds them.

/** The durable RnsAnnouncement — the trust-detail read projection and the
 *  materialise mutation's result row. camelCase wire keys mirror the ontology
 *  RnsAnnouncement row directly (PRD #278: contract fields are camelCase). */
export const rnsAnnouncementDtoSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    kind: z.string(),
    headline: z.string(),
    body: z.string(),
    status: z.enum(['drafted', 'approved', 'dispatched', 'withdrawn']),
    originatedFromDraftId: z.string().nullable(),
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    dispatchedAt: z.string().nullable(),
    rnsReference: z.string().nullable(),
    distributor: z.string().nullable(),
    createdAt: z.string(),
});
export type RnsAnnouncementDto = z.infer<typeof rnsAnnouncementDtoSchema>;

/** The InvestmentTrustPolicyBreach the approve-materialise mutation creates
 *  (camelCase wire keys mirroring the ontology row). */
export const itpBreachDtoSchema = z.object({
    id: z.string(),
    trustId: z.string(),
    ruleKind: z.string(),
    severity: z.enum(['approaching', 'breach', 'material']),
    rnsTrigger: z.boolean(),
    remediationPlan: z.string(),
    raisedAt: z.string(),
    status: z.string(),
    originFindingId: z.string(),
});
export type ItpBreachDto = z.infer<typeof itpBreachDtoSchema>;

export const trustDetailResponseSchema = z.object({
    trust: investmentTrustRowSchema.nullable(),
    exposures: z.array(exposureSnapshotRowSchema),
    policyClauses: z.array(policyClauseRowSchema),
    gearing: z.array(gearingSnapshotRowSchema),
    discounts: z.array(discountSnapshotRowSchema),
    shareholders: z.array(shareholderRowSchema),
    continuationVotes: z.array(continuationVoteRowSchema),
    brokerCoverage: z.array(brokerCoverageRowSchema),
    // RnsAnnouncement is an ontology Object Type read via ctx.read and projected
    // by the service — a real schema, not an ingestion-source passthrough.
    rnsAnnouncements: z.array(rnsAnnouncementDtoSchema),
    fundReportDrafts: z.array(fundReportDraftRowSchema),
});
export type TrustDetailResponse = z.infer<typeof trustDetailResponseSchema>;

import { errorBody } from '../schemas/common';

export const viewPagesContract = c.router(
    {
        getFirmOverview: {
            method: 'GET',
            path: '/view/firm-overview',
            responses: { 200: firmOverviewResponseSchema, 403: errorBody },
            summary: 'Firm Overview composed payload',
        },
        getFinancialPerformance: {
            method: 'GET',
            path: '/view/financial-performance',
            responses: { 200: financialPerformanceResponseSchema, 403: errorBody },
            summary:
                'Financial Performance — portfolio economics (income / fees / net flows / assets) per firm / category / manager from AccountPerformancePeriod.',
        },
        getInvestmentPerformance: {
            method: 'GET',
            path: '/view/investment-performance',
            responses: { 200: investmentPerformanceResponseSchema, 403: errorBody },
            summary:
                'Investment Performance — per-account multi-horizon returns + firm aggregate return (from AccountPerformanceReturns / Period).',
        },
        getBenchmarking: {
            method: 'GET',
            path: '/view/benchmarking',
            responses: { 200: benchmarkingResponseSchema, 403: errorBody },
            summary:
                'Benchmarking — firm performance vs the ARC Private Client Indices (AccountPerformanceReturns mapped to ARC bands via BenchmarkIndex/Level).',
        },
        getOperationalIntelligence: {
            method: 'GET',
            path: '/view/operational-intelligence',
            responses: { 200: operationalIntelligenceResponseSchema, 403: errorBody },
            summary:
                'Operational Intelligence — recon-break / overdrawn-cash / order-blotter signals',
        },
        getCustodianAnalytics: {
            method: 'GET',
            path: '/view/custodian-analytics',
            responses: { 200: custodianAnalyticsResponseSchema, 403: errorBody },
            summary: 'Custodian Analytics — per-custodian feed coverage (positions / securities)',
        },
        getBoardReporting: {
            method: 'GET',
            path: '/view/board-reporting',
            responses: { 200: viewAggregatesResponseSchema, 403: errorBody },
            summary: 'Board Reporting — dashboard_aggregates pass-through',
        },
        getFundManagementList: {
            method: 'GET',
            path: '/view/fund-management/list',
            responses: { 200: fundManagementListResponseSchema, 403: errorBody },
            summary: 'Fund Management top-level grid composed payload',
        },
        getTrustDetail: {
            method: 'GET',
            path: '/view/fund-management/:trustId',
            pathParams: z.object({ trustId: z.string() }),
            responses: { 200: trustDetailResponseSchema, 403: errorBody },
            summary: 'Per-trust detail composed for all subscreens',
        },

        // ------------------------------------------------------------------
        // Fund-management mutations (OLD_CODE.html #2 replacement for
        // `materialise-rns-announcement`, `submit-rns-announcement`,
        // `approve-investment-trust-policy-finding-materialise`). Both dispatch
        // through ontology Actions (ADR-0008): MaterialiseRnsAnnouncement and
        // ApproveInvestmentTrustPolicyFinding respectively.
        // ------------------------------------------------------------------
        materialiseRnsAnnouncement: {
            method: 'POST',
            path: '/view/fund-management/rns/materialise',
            body: z.object({ draftId: z.string(), approvedBy: z.string().optional() }),
            responses: staffActionResponses,
            summary: 'Materialise an RnsAnnouncement row from an approved CHARTER FundReportDraft.',
        },
        approveInvestmentTrustPolicyFindingMaterialise: {
            method: 'POST',
            path: '/view/fund-management/policy-findings/:id/approve-materialise',
            pathParams: z.object({ id: z.string() }),
            // The endpoint collects the real workflow decisions the
            // ApproveInvestmentTrustPolicyFinding Action accepts (ADR-0018
            // write-side rule): the reviewer may override the proposed severity
            // / RNS-trigger and attach an approval note. Omitted → the Action
            // falls back to the finding's proposed values.
            body: z
                .object({
                    severityOverride: z.enum(['approaching', 'breach', 'material']).optional(),
                    rnsTriggerOverride: z.boolean().optional(),
                    approvalNote: z.string().optional(),
                })
                .optional(),
            responses: staffActionResponses,
            summary:
                'Approve a BEACON InvestmentTrustPolicyFinding and materialise the durable breach row (ADR-0003).',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
